import React, {ReactElement, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Control, FieldArray, FieldArrayMethodProps, useFormContext, useWatch} from 'react-hook-form'
import {Checkbox, FormControlLabel, Grid, IconButton, Paper} from '@mui/material'
import TextInput, {MaskProps} from '../../common/form/TextInput'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {UseFieldArrayRemove} from "react-hook-form/dist/types/fieldArray";
import {HolidayForm} from "../../app/types";
import {ActionFabButton} from "../../common/components/ActionFabButton";

type Props = {
    control: Control<any>
    getValues: (payload?: string | string[]) => Object
    fields: Record<"id", string>[]
    remove: UseFieldArrayRemove
    insert: (index: number, value: (FieldArray<HolidayForm, "holidayPeriods"> | FieldArray<HolidayForm, "holidayPeriods">[]), options?: FieldArrayMethodProps) => void
    update: (index: number, value: FieldArray<HolidayForm, "holidayPeriods">) => void
}

const timeMaskProps: MaskProps = {
    mask: '#0:00',
    definitions: {
        '#': /[0-9]/,
    },
}

export const HolidayPeriodForm = ({ fields, control, getValues, remove, update, insert }: Props) => {
    const { t } = useTranslation()

    return (
        <>
            {fields.map((item, index) => (
                <Grid container item spacing={4} key={item.id} >
                    {index === 0  && <Grid container item xs={10} justifyContent={'center'}>
                        <ActionFabButton small={true} size={'small'} onClick={
                            () =>
                                insert(index,{
                                    from: null,
                                    to: null,
                                    eventName: '',
                                    allDay: true,
                                    recurringDays: null,
                                    startTime: null,
                                    endTime: null,
                                    type: 'period'
                                })
                        } label={t('schedules.add-period')} enabled={true} color={'primary'} Icon={AddIcon} />
                    </Grid>}
                    <Grid item md={10}>
                        <EventForm index={index} control={control} update={update} getValues={getValues} />
                    </Grid>
                    <Grid item container spacing={2} md={2} alignItems={'center'} justifyContent={'center'} flexWrap={'nowrap'}>
                        {fields.length > 1 && (
                            <Grid item>
                                <IconButton
                                    aria-label='delete'
                                    component='div'
                                    onClick={() => remove(index)}
                                >
                                    <DeleteIcon fontSize='inherit' />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container item xs={10} justifyContent={'center'}>
                        <ActionFabButton small={true} size={'small'} onClick={
                            () =>
                                insert(index +1,{
                                    from: null,
                                    to: null,
                                    eventName: '',
                                    allDay: true,
                                    recurringDays: null,
                                    startTime: null,
                                    endTime: null,
                                    type: 'period'
                                })
                        } label={t('schedules.add-period')} enabled={true} color={'primary'} Icon={AddIcon} />
                    </Grid>
                </Grid>
            ))}
        </>
    )
}

const EventForm = ({
                       index,
                       control,
                       update,
                       getValues,
                   }: {
    index: number
    control: Control<any>
    update: (index: number, period: any) => void
    getValues: (payload?: string | string[]) => Object
}) => {
    const { t } = useTranslation()

    const { clearErrors } = useFormContext()

    const isAllDay = useWatch({
        name: `holidayPeriods.${index}.allDay`,
        control: control,
    })

    const type = useWatch({
        name: `holidayPeriods.${index}.type`,
        control: control,
    })

    const typeOptions = useMemo(() => [
        {
            value: 'period', label: t(`schedules.holidays.edit.types.period`)
        },
        {
            value: 'weekly_recurring', label: t(`schedules.holidays.edit.types.weekly_recurring`)
        },
    ], [t])

    const dayOptions = useMemo(() => [
        {
            value: 'MONDAY', label: t(`general.days.MONDAY`)
        },
        {
            value: 'TUESDAY', label: t(`general.days.TUESDAY`)
        },
        {
            value: 'WEDNESDAY', label: t(`general.days.WEDNESDAY`)
        },
        {
            value: 'THURSDAY', label: t(`general.days.THURSDAY`)
        },
        {
            value: 'FRIDAY', label: t(`general.days.FRIDAY`)
        },
        {
            value: 'SATURDAY', label: t(`general.days.SATURDAY`)
        },
        {
            value: 'SUNDAY', label: t(`general.days.SUNDAY`)
        }
    ], [t])

    const AllDayCheckBox: ReactElement = useMemo(
        () => (
            <Checkbox
                checked={isAllDay}
                onChange={event => {
                    update(index, {
                        ...getValues(`holidayPeriods.${index}`),
                        allDay: event.target.checked,
                        ...(isAllDay ? { startTime: '', endTime: '' } : {}),
                    })
                    clearErrors('holidayPeriods')
                }}
            />
        ),
        [isAllDay, index, update, getValues, clearErrors],
    )

    const TimeSlots = useMemo(
        () => () =>
            isAllDay ? (
                <></>
            ) : (
                <>
                    <HolidayBox width={6}>
                        <TextInput required={true} label={t('schedules.holidays.startTime')}
                                   name={`holidayPeriods.${index}.startTime`} control={control}
                                   maskProps={timeMaskProps} />
                    </HolidayBox>
                    <HolidayBox width={6}>
                        <TextInput required={true} label={t('schedules.holidays.endTime')}
                                   name={`holidayPeriods.${index}.endTime`} control={control}
                                   maskProps={timeMaskProps} />
                    </HolidayBox>
                </>
            ),
        [isAllDay, index, control, t],
    )

    return (
        <Paper sx={{ p: 4 }}>
            <Grid container spacing={4}>
                <HolidayBox width={12}>
                    <TextInput required={true} label={t('schedules.holidays.event-name')}
                               name={`holidayPeriods.${index}.eventName`} control={control} />
                </HolidayBox>

                <HolidayBox width={6}>
                    <TextInput required={true} label={t('numbers.table.type')}
                               name={`holidayPeriods.${index}.type`} control={control} options={typeOptions} />
                </HolidayBox>
                <HolidayBox width={6}>
                    <></>
                </HolidayBox>
                {
                    type === 'weekly_recurring' ?
                        <HolidayBox width={12}>
                            <TextInput required={true} label={t('schedules.holidays.edit.section.days')}
                                       name={`holidayPeriods.${index}.recurringDays`} control={control} options={dayOptions} isCheckbox={true} />
                        </HolidayBox> :
                        <>
                            <HolidayBox width={6}>
                                <TextInput required={true} label={t('schedules.holidays.from')}
                                           name={`holidayPeriods.${index}.from`} control={control} isDate={true} />
                            </HolidayBox>
                            <HolidayBox width={6}>
                                <TextInput label={t('schedules.holidays.to')} name={`holidayPeriods.${index}.to`} control={control}
                                           isDate={true} />
                            </HolidayBox>
                        </>
                }
                <HolidayBox width={12} align={'end'} justifyContent={'start'}>
                    <FormControlLabel control={AllDayCheckBox} label={t('schedules.holidays.all-day') as string} />
                </HolidayBox>
                <TimeSlots />
            </Grid>
        </Paper>
    )
}

function HolidayBox({
                        children,
                        align,
                        width,
                        justifyContent = 'center',
                    }: { children: any; align?: string; width?: number; justifyContent?: string }) {
    return (
        <Grid container={!!align} item xs={12} sm={12} md={width || 3} lg={width || 3} alignItems={align}
              justifyContent={justifyContent}>
            {children}
        </Grid>
    )
}
